import { ContentType, type Content, type ContentHref } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import {
  selectApiWithPendingChanges,
  selectApiWithPendingChangesRelationsToAndFromMap,
  selectApiWithPendingChangesWithoutDeletes,
  selectContentItem,
} from '@newStore/documentApi/documentApiSelectors';
import type { RelationsToAndFromMap } from '@newStore/documentApi/documentApiTypes';
import { getPathToRootHrefs } from '@newStore/externalData/externalDataHelpers';
import { createTypedSelector, getSortedChildHrefsFromRelations } from '@newStore/genericHelpers';
import { selectKrcReferenceFrameContent } from '@newStore/referenceFrames/referenceFramesSelectors';
import { NodeType } from '@nodeTypeConfig/configTypes';
import type {
  KrcAgeRangeItemNode,
  KrcAgeRangeNode,
  KrcCurriculumClusterNode,
  KrcCurriculumDisciplineNode,
  KrcCurriculumDomainNode,
  KrcCurriculumGoalNode,
  KrcCurriculumNode,
  KrcCurriculumSubDomainNode,
  KrcGoalSetItemNode,
  KrcGoalSetNode,
} from '@newStore/documentUI/documentUITypes';

const krcTypesWithLeadingDot = new Set<ContentType>([
  ContentType.KRC_CURRICULUM_DOMAIN,
  ContentType.KRC_GOAL_SET_ITEM,
  ContentType.KRC_CURRICULUM_GOAL,
]);

export const getKrcExtendedIdentifier = (
  relationsMap: RelationsToAndFromMap,
  contentMap: Record<ContentHref, Content>,
  href: ContentHref,
): string => {
  const content = contentMap[href];
  if (!content) {
    return '';
  }

  return (
    getPathToRootHrefs(relationsMap, href)
      .map((href) => contentMap[href])
      .filter((pathContent): pathContent is Content => Boolean(pathContent))
      .reverse()
      .reduce((acc, pathContent) => {
        const identifier = pathContent.identifiers?.[0] || '';
        if (!identifier) {
          return acc;
        }

        const separator =
          acc && krcTypesWithLeadingDot.has(pathContent.type as ContentType) ? '.' : '';
        return `${acc}${separator}${identifier}`;
      }, '') ||
    content.identifiers?.[0] ||
    ''
  );
};

/**
 * Builds a Map keyed by every content href in `contentMap` to its KRC extended identifier
 * (e.g. `4.3GL2.6`). Entries with an empty identifier are skipped. Precomputing this once per
 * state avoids re-walking the parent chain in node selectors that run for every visible tree
 * node.
 */
export const getKrcExtendedIdentifierMap = (
  relationsMap: RelationsToAndFromMap,
  contentMap: Record<ContentHref, Content>,
): Map<ContentHref, string> => {
  const identifierMap = new Map<ContentHref, string>();
  for (const key of Object.keys(contentMap) as ContentHref[]) {
    const identifier = getKrcExtendedIdentifier(relationsMap, contentMap, key);
    if (identifier) {
      identifierMap.set(key, identifier);
    }
  }
  return identifierMap;
};

export const selectKrcExtendedIdentifierMap = createTypedSelector(
  [
    (state: RootState) => selectApiWithPendingChanges(state).content,
    (state: RootState) => selectApiWithPendingChangesRelationsToAndFromMap(state),
  ],
  (contentMap, relationsMap): Map<ContentHref, string> =>
    getKrcExtendedIdentifierMap(relationsMap, contentMap),
);

export const getKrcAgeRangeItemChildrenByExtendedIdentifierMap = (
  relationsMap: RelationsToAndFromMap,
  contentMap: Record<ContentHref, Content>,
): Record<ContentHref, { href: ContentHref; identifier: number; fullIdentifier: string }[]> => {
  const byExtendedId: Record<
    string,
    { href: ContentHref; identifier: number; fullIdentifier: string }[]
  > = {};
  const ageRangeItemHrefs: { href: ContentHref; extendedIdentifier: string }[] = [];

  for (const key of Object.keys(contentMap) as ContentHref[]) {
    const content = contentMap[key];
    if (content.type !== ContentType.KRC_AGE_RANGE_ITEM) continue;

    const href = content.$$meta.permalink;
    const extendedIdentifier = getKrcExtendedIdentifier(relationsMap, contentMap, href);
    if (!extendedIdentifier) continue;

    ageRangeItemHrefs.push({ href, extendedIdentifier });

    const goals = getSortedChildHrefsFromRelations(relationsMap.to[href])
      .map((childHref) => contentMap[childHref])
      .filter(
        (c): c is Content =>
          c?.type === ContentType.KRC_CURRICULUM_GOAL && Boolean(c.identifiers?.[0]?.trim()),
      )
      .map((c) => {
        const id = c.identifiers![0].trim();
        return {
          href: c.$$meta.permalink,
          identifier: Number.parseInt(id, 10),
          fullIdentifier: `${extendedIdentifier}.${id}`,
        };
      });

    const existing = byExtendedId[extendedIdentifier];
    if (existing) {
      existing.push(...goals);
    } else {
      byExtendedId[extendedIdentifier] = goals;
    }
  }

  const result: Record<
    ContentHref,
    { href: ContentHref; identifier: number; fullIdentifier: string }[]
  > = {};
  for (const { href, extendedIdentifier } of ageRangeItemHrefs) {
    result[href] = byExtendedId[extendedIdentifier];
  }
  return result;
};

export const selectKrcAgeRangeItemChildrenByExtendedIdentifierMap = createTypedSelector(
  [
    (state: RootState) => selectApiWithPendingChangesWithoutDeletes(state).content,
    (state: RootState) => selectApiWithPendingChangesRelationsToAndFromMap(state),
  ],
  (
    contentMap,
    relationsMap,
  ): Record<ContentHref, { href: ContentHref; identifier: number; fullIdentifier: string }[]> => {
    return getKrcAgeRangeItemChildrenByExtendedIdentifierMap(relationsMap, contentMap);
  },
);

export const selectKrcCurriculumNode = createTypedSelector(
  (state: RootState, href: ContentHref) => selectContentItem(state, href),
  (content: Content): KrcCurriculumNode => ({
    href: content.$$meta.permalink,
    key: content.key,
    title: content.title || '',
    type: NodeType.KRC_CURRICULUM,
  }),
);

export const selectKrcCurriculumDisciplineNode = createTypedSelector(
  (state: RootState, href: ContentHref) => selectContentItem(state, href),
  (content: Content): KrcCurriculumDisciplineNode => ({
    href: content.$$meta.permalink,
    key: content.key,
    identifier: content.identifiers?.[0] || '',
    title: content.title || '',
    type: NodeType.KRC_CURRICULUM_DISCIPLINE,
  }),
);

export const selectKrcCurriculumDomainNode = createTypedSelector(
  [
    (state: RootState, href: ContentHref) => selectContentItem(state, href),
    (state: RootState) => selectKrcExtendedIdentifierMap(state),
  ],
  (content: Content, identifierMap): KrcCurriculumDomainNode => ({
    href: content.$$meta.permalink,
    key: content.key,
    identifier: identifierMap.get(content.$$meta.permalink) || '',
    title: content.title || '',
    type: NodeType.KRC_CURRICULUM_DOMAIN,
  }),
);

export const selectKrcCurriculumSubDomainNode = createTypedSelector(
  (state: RootState, href: ContentHref) => selectContentItem(state, href),
  (content: Content): KrcCurriculumSubDomainNode => ({
    href: content.$$meta.permalink,
    key: content.key,
    title: content.title || '',
    type: NodeType.KRC_CURRICULUM_SUBDOMAIN,
  }),
);

export const selectKrcCurriculumClusterNode = createTypedSelector(
  (state: RootState, href: ContentHref) => selectContentItem(state, href),
  (content: Content): KrcCurriculumClusterNode => ({
    href: content.$$meta.permalink,
    key: content.key,
    title: content.title || '',
    type: NodeType.KRC_CURRICULUM_CLUSTER,
  }),
);

export const selectKrcCurriculumGoalNode = createTypedSelector(
  [
    (state: RootState, href: ContentHref) => selectContentItem(state, href),
    (state: RootState) => selectKrcExtendedIdentifierMap(state),
  ],
  (content: Content, identifierMap): KrcCurriculumGoalNode => ({
    href: content.$$meta.permalink,
    key: content.key,
    identifier: identifierMap.get(content.$$meta.permalink) || '',
    title: content.title || '',
    description: content.description || '',
    type: NodeType.KRC_CURRICULUM_GOAL,
  }),
);

export const selectKrcGoalSetNode = createTypedSelector(
  (state: RootState, href: ContentHref) => selectContentItem(state, href),
  (content: Content): KrcGoalSetNode => ({
    href: content.$$meta.permalink,
    key: content.key,
    identifier: content.identifiers?.[0] || '',
    title: content.title || '',
    description: content.description || '',
    type: NodeType.KRC_GOAL_SET,
  }),
);

export const selectKrcAgeRangeNode = createTypedSelector(
  (state: RootState, href: ContentHref) => selectContentItem(state, href),
  (content: Content): KrcAgeRangeNode => ({
    href: content.$$meta.permalink,
    key: content.key,
    identifier: content.identifiers?.[0] || '',
    title: content.title || '',
    type: NodeType.KRC_AGE_RANGE,
  }),
);

export const selectFirstThemeContent = (state: RootState, href: ContentHref): Content | null => {
  const content = selectContentItem(state, href);
  const themeHref = content?.themes?.[0] as ContentHref | undefined;
  if (!themeHref) {
    return null;
  }
  return selectKrcReferenceFrameContent(state)[themeHref] ?? null;
};

export const selectKrcGoalSetItemNode = createTypedSelector(
  [
    (state: RootState, href: ContentHref) => selectContentItem(state, href),
    (state: RootState) => selectKrcExtendedIdentifierMap(state),
  ],
  (content: Content, identifierMap): KrcGoalSetItemNode => ({
    href: content.$$meta.permalink,
    key: content.key,
    themes: content.themes || [],
    title: content.title || '',
    identifier: identifierMap.get(content.$$meta.permalink) || '',
    customRender: true,
    type: NodeType.KRC_GOAL_SET_ITEM,
  }),
);

export const selectKrcAgeRangeItemNode = createTypedSelector(
  [
    (state: RootState, href: ContentHref) => selectContentItem(state, href),
    (state: RootState) => selectKrcExtendedIdentifierMap(state),
  ],
  (content: Content, identifierMap): KrcAgeRangeItemNode => ({
    href: content.$$meta.permalink,
    key: content.key,
    themes: content.themes || [],
    title: content.title || '',
    identifier: identifierMap.get(content.$$meta.permalink) || '',
    customRender: true,
    type: NodeType.KRC_AGE_RANGE_ITEM,
  }),
);
